// Set-offer card: adds the free pieces alongside the core on the native ATC click.
// Contract with blocks/skullette-set-offer.liquid:
//   [data-set-offer] container - data-core-cents, data-set-handle
//   [data-set-offer-free] JSON islands: { vid, cents, preB?, preE?, handle }
// Same interception as assets/skullette-set-picker.js: one /cart/add.js items[] call
// for the free pieces, then form.requestSubmit() so Horizon opens/morphs the drawer once.
// The ATC button gets data-set-base-cents = core cents so skullette-pdp.js prints
// "Add to cart · $core" (free pieces are $0 through the store's automatic discount).
// On failure: log, then fall through to the native single add - the shopper is never stuck.

function initOffer(card) {
  if (card.__skOfferInit) return;
  card.__skOfferInit = true;
  const frees = [...card.querySelectorAll('[data-set-offer-free]')]
    .map((el) => {
      try {
        return JSON.parse(el.textContent || 'null');
      } catch {
        return null;
      }
    })
    .filter((m) => m && m.vid);
  const section = card.closest('section, .shopify-section') || document;
  const form = section.querySelector('product-form-component form');
  const atcButton = form?.querySelector('button[type="submit"][name="add"]');
  const setHandle = card.getAttribute('data-set-handle') || '';
  // Discount gate (Shopify Function afl-set-discount): cart attribute _afl_lp=<gate> AND line property _afl_lp on every
  // counted line. The core rides the native form submit, so its properties are hidden inputs appended to the form.
  const gate = card.getAttribute('data-set-gate') || '';
  const setName = card.getAttribute('data-set-name') || '';
  const coreCents = parseInt(card.getAttribute('data-core-cents'), 10) || 0;
  // Material rows inside the card proxy the theme's own variant-picker radios (hidden on set pages)
  // so Horizon still owns the variant switch: price, URL, hidden id input, section morph.
  // hide ONLY the picker element - its closest .group-block is the whole buy box (learned the hard way)
  const themePicker = section.querySelector('variant-picker');
  if (themePicker) themePicker.style.display = 'none';
  // Horizon morphs its own regions on a variant switch, not this card: repaint the card from the
  // per-variant island. Paint IMMEDIATELY on the tap (no waiting for the section fetch), then the
  // hidden-id poll below reconciles if Horizon lands on a different variant.
  let variants = {};
  try {
    variants = JSON.parse(card.querySelector('[data-set-offer-variants]')?.textContent || '{}');
  } catch {
    /* no island = single price, nothing to repaint */
  }
  const money = (cents) => {
    const base = `$${(cents / 100).toFixed(2)}`;
    return base.endsWith('.00') ? base.slice(0, -3) : base;
  };
  const idInput = form?.querySelector('input[name="id"]');
  // wanted = the variant the shopper LAST tapped. Horizon's section fetches can land out of order when
  // she taps quickly; any hidden-id value that is not the wanted one is stale and must not repaint.
  let wanted = idInput?.value;
  let painted = idInput?.value;
  const liveButton = () => section.querySelector('product-form-component button[type="submit"][name="add"]') || atcButton;
  const labelSpan = () => liveButton()?.querySelector('.add-to-cart-text__content > span') || liveButton()?.querySelector('.add-to-cart-text__content');
  const paintVariant = (vid) => {
    const v = variants[vid];
    if (!v) return;
    painted = vid;
    const coreEl = card.querySelector('.sk-offer__row .pban__now');
    if (coreEl) coreEl.textContent = money(v.price);
    const yours = card.querySelector('[data-set-offer-yours]');
    if (yours) yours.textContent = money(v.price);
    const apart = card.querySelector('[data-set-offer-apart]');
    if (apart) apart.textContent = money(v.price + (parseInt(apart.getAttribute('data-free-cents'), 10) || 0));
    card.querySelectorAll('.sk-offer__mat').forEach((m) => {
      const r = m.querySelector('[data-set-offer-mat]');
      const on = r && r.value === v.option;
      m.classList.toggle('is-on', !!on);
      if (r) r.checked = !!on;
    });
    card.setAttribute('data-core-cents', String(v.price));
    const btn = liveButton();
    if (btn) {
      btn.setAttribute('data-set-base-cents', String(v.price));
      btn.setAttribute('data-set-payable-cents', String(v.price));
      // write the label in the same frame (no event round-trip): keep the verb, swap the number
      const span = labelSpan();
      if (span) {
        const verb = (span.textContent || '').split(' · ')[0].trim() || 'Add to Bag';
        span.textContent = verb + ' · ' + money(v.price);
      }
    }
    document.dispatchEvent(new CustomEvent('skullette:set:change', { detail: { kind: 'set', members: frees } }));
  };
  card.querySelectorAll('[data-set-offer-mat]').forEach((radio) => {
    radio.addEventListener('change', () => {
      const vid = Object.keys(variants).find((k) => variants[k].option === radio.value);
      if (vid) {
        wanted = vid;
        paintVariant(vid); // instant
      }
      // hand the switch to Horizon after a short trailing debounce: rapid taps then cost ONE section
      // fetch instead of one aborted fetch per tap (Horizon logs each abort as an error)
      clearTimeout(card.__skProxyTimer);
      card.__skProxyTimer = setTimeout(() => {
        const target = section.querySelector('variant-picker input[type="radio"][value="' + CSS.escape(radio.value) + '"]');
        if (target && !target.checked) target.click(); // Horizon: URL, hidden id, gallery
      }, 160);
    });
  });
  if (idInput && Object.keys(variants).length > 1) {
    // Horizon's morph rewrites the button label to the bare verb; re-stamp in the SAME mutation
    // batch (observer callback runs as a microtask right after the DOM change), so no bare frame paints.
    const reconcile = () => {
      const cur = idInput.value;
      if (cur === wanted && painted !== wanted) paintVariant(wanted);
      const span = labelSpan();
      if (span && painted === wanted && !(span.textContent || '').includes('$')) paintVariant(wanted);
    };
    new MutationObserver(reconcile).observe(section, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['value'] });
    setInterval(reconcile, 500); // safety net only
  }
  if (!form || !atcButton || !frees.length) return;

  if (gate && !form.querySelector('input[name="properties[_afl_lp]"]')) {
    for (const [k, v] of [['properties[_afl_lp]', gate], ['properties[Set]', setName]]) {
      if (!v) continue;
      const inp = document.createElement('input');
      inp.type = 'hidden';
      inp.name = k;
      inp.value = v;
      form.appendChild(inp);
    }
  }

  atcButton.setAttribute('data-set-base-cents', String(coreCents));
  atcButton.setAttribute('data-set-payable-cents', String(coreCents));
  // skullette-pdp.js attaches its set:change listener as a separate module; fire now and again after load
  const announce = () => document.dispatchEvent(new CustomEvent('skullette:set:change', { detail: { kind: 'set', members: frees } }));
  announce();
  setTimeout(announce, 0);
  window.addEventListener('load', announce, { once: true });

  const addFrees = async () => {
    if (gate) {
      // cart attribute first: the function reads it on every cart change
      await fetch(window.Theme?.routes?.cart_update_url ?? '/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { _afl_lp: gate } }),
      });
    }
    const items = frees.map((m) => {
      const properties = { _attach_source: 'set-offer', _sk_set: setHandle };
      if (gate) {
        properties._afl_lp = gate;
        if (setName) properties.Set = setName;
      }
      if (m.preB) {
        properties._preorder = 'true';
        properties._preorder_batch = m.preB;
        if (m.preE) properties._preorder_eta = m.preE;
      }
      return { id: m.vid, quantity: 1, properties };
    });
    const res = await fetch(window.Theme?.routes?.cart_add_url ?? '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    });
    if (!res.ok) throw new Error(`cart/add ${res.status}`);
  };

  if (form.__skOfferHooked) return;
  form.__skOfferHooked = true;
  let busy = false;
  const run = async (viaSubmit) => {
    if (form.dataset.skSetHandled || busy) return;
    busy = true;
    try {
      await addFrees();
    } catch (error) {
      console.error('set offer free-piece add failed', error);
    } finally {
      form.dataset.skSetHandled = 'true';
      if (!viaSubmit) form.requestSubmit();
      setTimeout(() => delete form.dataset.skSetHandled, 600);
      busy = false;
    }
  };
  // delegated: Horizon may replace the button node on a variant morph; the section persists
  section.addEventListener('click', async (event) => {
    const btn = event.target.closest && event.target.closest('product-form-component button[type="submit"][name="add"]');
    if (!btn || form.dataset.skSetHandled || busy) return;
    event.preventDefault();
    await run(false);
  });
  form.addEventListener('submit', () => {
    if (form.dataset.skSetHandled || busy) return;
    run(true);
  });
}

const scan = () => document.querySelectorAll('[data-set-offer]').forEach(initOffer);
scan();
// Variant switches morph the PDP section - re-init any card the morph brings in.
new MutationObserver(scan).observe(document.body, { childList: true, subtree: true });
